import { useState } from 'react';

import { ChartFrame } from '../charts/ChartFrame';
import { TEMPLATES } from '../charts/registry';
import type { ChartData } from '../types';

/**
 * The style gallery, at `/preview`.
 *
 * Every form rendered against fixture data, with no backend and no session. It
 * exists because the chart layer is the part of this app you cannot review by
 * reading it — and because a real query needs an app_id, a handoff token and a
 * live upstream, which is three dependencies too many for "does the new bar
 * chart look right?".
 *
 * It doubles as the reference for anyone adding a form: if it doesn't look
 * right beside the others here, it isn't finished.
 */

const MONTHS = [
  '2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06',
  '2026-07', '2026-08', '2026-09', '2026-10', '2026-11', '2026-12',
];

/** A single measure over time — trends, stat tiles. */
const timeSeries: ChartData = {
  columns: ['month', 'downloads'],
  rows: MONTHS.map((month, i) => [
    month,
    Math.round(410_000 + i * 34_000 + Math.sin(i * 1.7) * 46_000),
  ]),
};

/** The same period axis broken down by a second dimension — stacks, multi-line. */
const timeSeriesByPlatform: ChartData = {
  columns: ['month', 'platform', 'downloads'],
  rows: MONTHS.flatMap((month, i) => [
    [month, 'Android', Math.round(240_000 + i * 21_000 + Math.cos(i) * 24_000)],
    [month, 'iOS', Math.round(170_000 + i * 13_000 + Math.sin(i * 2.1) * 19_000)],
  ]),
};

/** Named things with one measure — bars, leaderboards, donuts. */
const byCountry: ChartData = {
  columns: ['country', 'downloads'],
  rows: [
    ['United States', 2_940_000],
    ['India', 2_115_000],
    ['Brazil', 1_480_000],
    ['Indonesia', 1_162_000],
    ['United Kingdom', 866_000],
    ['Germany', 741_000],
    ['Mexico', 690_000],
    ['France', 604_000],
    ['Japan', 512_000],
    ['Canada', 388_000],
    ['Australia', 301_000],
    ['Spain', 264_000],
  ],
};

/**
 * Shaped like the `top_charts` table behind Superset's Top Charts dashboard:
 * one row per app per chart type, ranked in query order, with the rating that
 * drives the star column.
 */
const topCharts: ChartData = {
  columns: ['type', 'app', 'avg_rating'],
  rows: [
    ['Free', 'Photo Studio', 4.6],
    ['Free', 'Fit Tracker', 4.42],
    ['Free', 'Budget Buddy', 4.7],
    ['Free', 'Night Sky', 4.18],
    ['Free', 'Recipe Box', 4.05],
    ['Free', 'Trail Notes', 3.9],
    ['Paid', 'Focus Timer', 4.8],
    ['Paid', 'Chart Pro', 4.55],
    ['Paid', 'Ledger', 4.3],
    ['Paid', 'Star Atlas', 4.12],
    ['Paid', 'Ink Journal', 3.95],
    ['Paid', 'Metronome+', 3.6],
    ['Grossing', 'Realm Quest', 4.65],
    ['Grossing', 'City Builder', 4.35],
    ['Grossing', 'Puzzle Kings', 4.22],
    ['Grossing', 'Farm Story', 4.02],
    ['Grossing', 'Battle Arena', 3.88],
    ['Grossing', 'Card Clash', 3.55],
  ],
};

/** Several unlike columns — the table. */
const detail: ChartData = {
  columns: ['app', 'publisher', 'rank', 'rating', 'downloads', 'revenue_usd'],
  rows: [
    ['Photo Studio', 'Lumen Labs', 1, 4.6, 2_940_000, 1_240_000],
    ['Fit Tracker', 'Northbeat', 2, 4.4, 2_115_000, 880_500],
    ['Budget Buddy', 'Cassava', 3, 4.7, 1_480_000, 612_400],
    ['Night Sky', 'Orrery', 4, 4.2, 1_162_000, 401_900],
    ['Recipe Box', 'Cassava', 5, 4.1, 866_000, 298_300],
  ],
};

const FIXTURES: Array<{ id: string; label: string; data: ChartData }> = [
  { id: 'countries', label: 'Named categories (12 countries)', data: byCountry },
  { id: 'months', label: 'One measure over 12 months', data: timeSeries },
  { id: 'split', label: 'Two series over 12 months', data: timeSeriesByPlatform },
  { id: 'topcharts', label: 'Top charts (3 types)', data: topCharts },
  { id: 'detail', label: 'Mixed columns (5 apps)', data: detail },
];

export const Gallery = () => {
  const [fixture, setFixture] = useState(FIXTURES[0]);

  return (
    <div className="app">
      <header className="app__header">
        <h1>Chart gallery</h1>
        <nav>
          {FIXTURES.map((f) => (
            <button
              key={f.id}
              className={fixture.id === f.id ? 'active' : ''}
              onClick={() => setFixture(f)}
            >
              {f.label}
            </button>
          ))}
        </nav>
      </header>

      <p className="hint">
        Every form against the same fixture, so they can be compared directly. Switch the data
        above to see how each form behaves when it is the wrong choice for the shape — that is
        as useful as seeing it when it is right.
      </p>

      <div className="card-grid gallery-grid">
        {TEMPLATES.map((t) => (
          <div className="gallery-item" key={t.id}>
            <ChartFrame title={t.name} description={t.suits} data={fixture.data}>
              <t.Component data={fixture.data} />
            </ChartFrame>
          </div>
        ))}
      </div>
    </div>
  );
};
